import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import {
    fetchChapterHighlights,
    getRecentDateStrings,
    fetchStockDataWithFallback,
} from "./dataFetcher";
import { getChapterHighlightStyle, formatStockFromRaw } from "./stockUtils";

import "./body.css";
import "./fave.css";

const parseArchiveDate = (dateStr) => {
    const parts = (dateStr || "").split("-");
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    if (!day || Number.isNaN(month) || !year) {
        return null;
    }

    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPrettyDate = (dateStr) => {
    const parsed = parseArchiveDate(dateStr);
    if (!parsed) {
        return dateStr;
    }

    return parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const computeTheoreticalGain = (faves = [], stockMap = {}) => {
    const initialInvestmentPerStock = 100;
    let totalBasis = 0;
    let totalCurrentValue = 0;

    faves.forEach((entry) => {
        const tickerKey = (entry.ticker || "").toLowerCase();
        const priceWhenSelected =
            typeof entry.price_when_selected === "number"
                ? entry.price_when_selected
                : typeof entry.price_when_selected === "string"
                    ? parseFloat(entry.price_when_selected)
                    : null;
        if (!priceWhenSelected || !Number.isFinite(priceWhenSelected)) {
            return;
        }

        const shares = initialInvestmentPerStock / priceWhenSelected;
        const latestStock = stockMap[tickerKey];
        const latestPrice =
            latestStock && typeof latestStock.currentPrice === "number"
                ? latestStock.currentPrice
                : null;

        totalBasis += initialInvestmentPerStock;
        totalCurrentValue += latestPrice ? shares * latestPrice : 0;
    });

    if (totalBasis === 0) {
        return null;
    }

    const gainPercent = ((totalCurrentValue - totalBasis) / totalBasis) * 100;
    return gainPercent;
};

function Archive() {
    const [archiveEntries, setArchiveEntries] = useState([]);
    const [highlightMap, setHighlightMap] = useState({});
    const [latestStockMap, setLatestStockMap] = useState({});
    const [theoreticalGain, setTheoreticalGain] = useState(null);
    useEffect(() => {
        window.scrollTo(0, 0);
        fetch(
            "https://investingfunctionstorage.blob.core.windows.net/alphavantage/faves.json"
        )
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to fetch response");
                }
                return response.json();
            })
            .then((data) => {
                const faves = Array.isArray(data["stockdata"]) ? data["stockdata"] : [];

                const archive = faves
                    .map((entry) => {
                        const rawPrice = entry.price_when_selected;
                        const parsedPrice =
                            typeof rawPrice === "number"
                                ? rawPrice
                                : typeof rawPrice === "string"
                                    ? parseFloat(rawPrice)
                                    : null;
                        return {
                            date: formatPrettyDate(entry.date),
                            ticker: entry.ticker,
                            priceWhenSelected: Number.isFinite(parsedPrice) ? parsedPrice : null,
                        };
                    })
                    .filter((entry) => entry.date && entry.ticker);

                const orderedArchive = archive.slice().reverse();
                setArchiveEntries(orderedArchive);
                loadLatestData(orderedArchive, faves);
            })
            .catch((error) => {
                console.error("There was a problem with the fetch operation:", error);
            });
        document.title = "Invest With Knowledge! - Fave Archive";
    }, []);

    const loadLatestData = async (entries = [], rawFaves = []) => {
        if (!entries.length) {
            setLatestStockMap({});
            setHighlightMap({});
            return;
        }

        const tickers = Array.from(
            new Set(
                entries
                    .map((entry) => (entry.ticker || "").toLowerCase())
                    .filter((ticker) => ticker.length > 0)
            )
        );

        if (!tickers.length) {
            setLatestStockMap({});
            setHighlightMap({});
            return;
        }

        try {
            const stockPayload = await fetchStockDataWithFallback(getRecentDateStrings(4));
            const stockMap = {};

            stockPayload.combined.forEach((stock) => {
                const tickerKey = (stock.ticker || "").toLowerCase();
                if (tickers.includes(tickerKey)) {
                    stockMap[tickerKey] = formatStockFromRaw(stock);
                }
            });

            setLatestStockMap(stockMap);

            const highlights = await fetchChapterHighlights(stockPayload.dateStr, tickers);
            setHighlightMap(highlights);

            const gain = computeTheoreticalGain(rawFaves, stockMap);
            setTheoreticalGain(gain);
        } catch (err) {
            console.error("Unable to load latest stock data for archive:", err);
            setLatestStockMap({});
            setHighlightMap({});
            setTheoreticalGain(null);
        }
    };

    function capitalizeFirstLetter(text) {
        return text.toLowerCase().replace(/(?:^|\s)\S/g, function (char) {
            return char.toUpperCase();
        });
    }

    const formatPrice = (value) => {
        if (typeof value !== "number" || Number.isNaN(value)) {
            return null;
        }
        return value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const formatPercent = (value) => {
        if (typeof value !== "number" || Number.isNaN(value)) {
            return null;
        }
        return `${value >= 0 ? "+" : "-"}${Math.abs(value).toFixed(2)}%`;
    };

    return (
        <section className="mainbody">
            <h2 className="responsiveheader">Fave Archive</h2>
            {theoreticalGain !== null ? (
                <p
                    className="centeredp"
                    title="Computed based on an even spread of cost basis over all the faves and their prices when chosen."
                >
                    Total Theoretical Gain/Loss:{" "}
                    <span
                        style={{
                            color:
                                theoreticalGain >= 0 ? "var(--better)" : "var(--worse)",
                        }}
                    >
                        {formatPercent(theoreticalGain)}
                    </span>
                </p>
            ) : null}
            {archiveEntries.map((entry, index) => {
                const tickerKey = (entry.ticker || "").toLowerCase();
                const highlightEntry = highlightMap[tickerKey];
                const latestStock = latestStockMap[tickerKey] || null;
                const displayName =
                    latestStock && latestStock.name ? latestStock.name : "";
                const displayIndustry =
                    latestStock && latestStock.industry ? latestStock.industry : "";
                const priceWhenSelected =
                    typeof entry.priceWhenSelected === "number"
                        ? entry.priceWhenSelected
                        : null;
                const latestPrice =
                    latestStock && typeof latestStock.currentPrice === "number"
                        ? latestStock.currentPrice
                        : null;
                const formattedPriceWhenSelected = formatPrice(priceWhenSelected);
                const formattedLatestPrice = formatPrice(latestPrice);
                const priceChangePercent =
                    typeof priceWhenSelected === "number" &&
                        typeof latestPrice === "number" &&
                        priceWhenSelected !== 0
                        ? ((latestPrice - priceWhenSelected) / priceWhenSelected) * 100
                        : null;
                const priceChangePercentText =
                    priceChangePercent !== null
                        ? `${priceChangePercent >= 0 ? "+" : "-"}${Math.abs(
                            priceChangePercent
                        ).toFixed(2)}%`
                        : null;
                const priceChangeStyle =
                    priceChangePercent !== null
                        ? {
                            color:
                                priceChangePercent >= 0
                                    ? "var(--better)"
                                    : "var(--worse)",
                        }
                        : null;

                return (
                    <div
                        className="archivediv"
                        style={getChapterHighlightStyle(highlightEntry)}
                        key={index}
                    >
                        <p className="centeredp">{entry.date}</p>
                        {displayName ? <p>Name: {displayName}</p> : null}
                        {displayIndustry ? (
                            <p>Industry: {capitalizeFirstLetter(displayIndustry)}</p>
                        ) : null}
                        <p>Ticker: {entry.ticker}</p>
                        <p>
                            Price When Chosen:{" "}
                            {formattedPriceWhenSelected
                                ? `$${formattedPriceWhenSelected}`
                                : "Unavailable"}
                        </p>
                        {formattedLatestPrice ? (
                            <p>
                                Price Today: ${formattedLatestPrice}
                                {formattedPriceWhenSelected && priceChangePercentText ? (
                                    <>
                                        {" "}
                                        (
                                        <span style={priceChangeStyle}>
                                            {priceChangePercentText}
                                        </span>
                                        )
                                    </>
                                ) : null}
                            </p>
                        ) : null}
                        <p className="centeredp">
                            <NavLink to={`/business/${entry.ticker.toLowerCase()}`}>
                                View More Details
                            </NavLink>
                        </p>
                    </div>
                );
            })}
        </section>
    );
}

export default Archive;
